/**
 * usePrices — single source of truth for every price string rendered in the UI.
 *
 * RevenueCat is the authority for prices. This hook reads live offerings via
 * iap.getOfferings() (which returns the locale-formatted StoreProduct.priceString
 * and an empty list when the store cannot be reached), then exposes Pro's price
 * strings resolved BY PRODUCT ID — see findPrice for why never by period.
 *
 * Components MUST consume this hook (or formatPlanLine below) instead of
 * hardcoding any "$" price literal. Because priceString comes from
 * StoreKit / Play Billing it is already formatted for the device locale
 * (handles $, GBP, EUR, JPY symbol position and decimals), so a non-USD
 * price slots in correctly with no code change here.
 *
 * While the live offerings resolve, every field is null and callers render
 * PRICE_PLACEHOLDER rather than a baked-in number. There is no local price
 * fallback anywhere: a wrong number renders exactly like a right one.
 */
import { useQuery } from "@tanstack/react-query";
import { IAP_PRODUCT_IDS, getOfferings, productKey, type IapOffering } from "@/lib/iap";

// 2026-07-15: `lifetime` is gone. It only ever carried the Transfer Organiser
// one-time price, and that product was withdrawn — Pro is all we sell now.
export interface PriceSet {
  /** Pro monthly price, e.g. "$5.99". Null until offerings resolve. */
  monthly: string | null;
  /** Pro annual price, e.g. "$59.99". Null until offerings resolve. */
  annual: string | null;
}

export type PricePeriod = keyof PriceSet;

/**
 * Price for ONE specific store product, or null when the store has not (yet)
 * offered it. Matching is on the product id via productKey, so an Android
 * `ndpromonth:monthly` resolves the same as an Apple `NDProMonth`.
 *
 * Look prices up by PRODUCT, never by billing period. The Nexus Raid Passport
 * added a second monthly subscription to the same RevenueCat offering, so a
 * period match ("first offering whose period is monthly") returns whichever of
 * Pro monthly / Passport monthly RevenueCat happens to list first — i.e. the
 * paywall's Pro price becomes a function of the drag-and-drop order of packages
 * in the RevenueCat dashboard. That is exactly the "plausible wrong number
 * renders like a right one" failure this module exists to prevent.
 */
export function findPrice(offerings: IapOffering[], productId: string): string | null {
  const want = productKey(productId);
  return offerings.find((o) => productKey(String(o.productId)) === want && o.priceString)
    ?.priceString ?? null;
}

/** Exported for tests. Pro's two prices, resolved by product id. */
export function toPriceSet(offerings: IapOffering[]): PriceSet {
  return {
    monthly: findPrice(offerings, IAP_PRODUCT_IDS.proMonthly),
    annual: findPrice(offerings, IAP_PRODUCT_IDS.proAnnual),
  };
}

/**
 * Read the live price set. Offerings are fetched once and cached for the
 * session; React Query dedupes concurrent callers so every paywall surface
 * shares one network read. Until the fetch resolves, every field is null —
 * callers render PRICE_PLACEHOLDER (a neutral state), never a baked-in number.
 */
export function usePrices(): { prices: PriceSet } {
  const { data } = useQuery({
    queryKey: ["iap", "offerings"],
    queryFn: getOfferings,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    // An empty answer is never final (2026-09-11): the store may not have
    // been reachable or configured yet. Keep asking every ten seconds until
    // a real price list arrives, then settle for the session.
    refetchInterval: (query) => ((query.state.data?.length ?? 0) > 0 ? false : 10_000),
  });
  return { prices: toPriceSet(data ?? []) };
}

/** Neutral placeholder shown in place of a price while offerings resolve. */
export const PRICE_PLACEHOLDER = "…";

/**
 * Build a price-plus-period line from a priceString and a period label,
 * never by concatenating a hardcoded currency symbol. A £/€/¥ priceString
 * slots in unchanged.
 *
 *   formatPlanLine("$5.99", "/mo") -> "$5.99/mo"
 *   formatPlanLine(null, "/mo")    -> "…/mo"
 */
export function formatPlanLine(priceString: string | null, periodLabel: string): string {
  return `${priceString ?? PRICE_PLACEHOLDER}${periodLabel}`;
}
